import {
  ProjectApplyItemWarning,
  ProjectDecisionWord,
  ProjectInquiryGroupStatus,
  ProjectRegisterMergeRefusal,
  ProjectRoomKey,
  ProjectWriteBlocker,
} from 'models/projects/projectEnums';
import { getRoomPart } from 'models/projects/projectRoomParts';
import type {
  PersonRef,
  ProjectDocumentsRoomPart,
  ProjectRoomItemPart,
  ProjectRoomPart,
} from 'models/projects/projectRoomParts';
import { areDifferent } from 'utils/array';
import type { AssessmentTaskCreated } from 'models/auditCriteriaGenericSets';

// Room aggregate for GET projects/{id}/rooms/{roomKey} and apply.

/**
 * The three values an apply result item's outcome can carry. Not one of the API's constants;
 * lives with the model.
 */
export const projectApplyOutcomes = ['applied', 'already-applied', 'failed'] as const;
export type ProjectApplyOutcome = (typeof projectApplyOutcomes)[number];

/** Code then name; an empty code is a real state (not yet numbered) so the name stands alone. */
export const getProjectItemTitle = (item: { code: string; name: string }): string => {
  return item.code.length > 0 ? `${item.code} ${item.name}` : item.name;
};

export class ProjectRoomItem {
  ownerId?: string;

  stateName?: string;

  tagIds: number[] = [];

  normIds: number[] = [];

  /**
   * The room's own record id, as text (ObjectiveId, NormId, RiskId, or a role Guid depending on
   * room).
   */
  itemId: string;

  code: string;

  /** The record's own ExternalId column (OBJ-, RSK-, DOC-); undefined for roles and the profile. */
  externalId?: string;

  name: string;

  description: string;

  /**
   * The record's own state column, per kind; its meaning belongs to the room, not to Project.
   * Undefined for a kind without one: roles (read the role part's `holders`), the inquiry rows (read
   * their part's `inquiry.taskId`), scope, profile and outcomes.
   */
  state?: number;

  /**
   * This record's own archived state. Since there is no per-item decision memory on the wire, it is
   * what marks a row set aside.
   * See `getProjectRecordedDecision` (`projectDecisionModel.ts`).
   */
  isArchived: boolean;

  /**
   * True when this row needs an answer before the room can confirm, by the room's own server-side
   * rule. A saved row that stays unresolved blocks the confirm.
   */
  unresolved: boolean;

  /**
   * What only some rooms know about this row: one part of the kind of the row (projectRoomParts.ts); undefined
   * for a row that knows nothing beyond these fields.
   */
  part?: ProjectRoomItemPart;

  constructor() {
    this.itemId = '';
    this.code = '';
    this.name = '';
    this.description = '';
    this.isArchived = false;
    this.unresolved = false;
  }

  /** Compares the row's own fields; its part is not compared (the room is replaced on every read). */
  isEqual(item: ProjectRoomItem): boolean {
    if (item.ownerId !== this.ownerId || item.stateName !== this.stateName) return false;
    if (areDifferent(item.tagIds, this.tagIds, (a, b) => a === b)) return false;
    if (areDifferent(item.normIds, this.normIds, (a, b) => a === b)) return false;
    let isEqual: boolean = true;
    if (item.itemId !== this.itemId) isEqual = false;
    if (item.code !== this.code) isEqual = false;
    if (item.externalId !== this.externalId) isEqual = false;
    if (item.name !== this.name) isEqual = false;
    if (item.description !== this.description) isEqual = false;
    if (item.state !== this.state) isEqual = false;
    if (item.isArchived !== this.isArchived) isEqual = false;
    if (item.unresolved !== this.unresolved) isEqual = false;

    return isEqual;
  }
}

/** One related record of a room item: a control of a risk (the risk row part's `controls`, risks room). */
export interface ProjectRoomRelated {
  id: string;

  code: string;

  name: string;
}

/** One comment of an inquiry's assignee on the inquiry task: a signal for whoever manages the project. */
export interface ProjectRoomInquirySignal {
  created: Date;

  /** Who wrote it; undefined from an API without the author. */
  authorId?: string;

  text: string;

  /** The comment's `commentTrailEntryId` on the task's comment trail. */
  entryId?: number;
}

/** Whether two reads say the same about who approves the kickoff documents (a changed approve mode re-renders). */
const isSameDocumentsPart = (
  a: ProjectDocumentsRoomPart | undefined,
  b: ProjectDocumentsRoomPart | undefined,
): boolean =>
  a?.approveMode === b?.approveMode &&
  a?.approveBlock === b?.approveBlock &&
  a?.directieUserId === b?.directieUserId &&
  a?.directieName === b?.directieName &&
  a?.directieRoleName === b?.directieRoleName;

/**
 * One room as it opens: the item set and its anchor task. No version token - an
 * apply is a plain synchronous write, and a re-read replaces the held room.
 */
export default class ProjectRoom {
  projectId: number;

  /** The room's key - its identity, and the route segment. */
  roomKey: ProjectRoomKey;

  /** The EntityTypes value this room covers. An attribute of the room, not its identity. */
  kind: number;

  /** The decision routes this room dispatches, as the server declared them. */
  actions: ProjectDecisionWord[];

  /**
   * The anchor task of this room's deliverable; an inquiry task a send creates is related to it. Undefined for a
   * room without an anchor (asset-answers).
   */
  deliverableTaskId?: number;

  /**
   * What only some rooms know about the read: one part of the kind of the read (projectRoomParts.ts); undefined
   * for a room that knows nothing beyond its rows.
   */
  part?: ProjectRoomPart;

  /** Why the caller cannot write this room now (the server's apply gate); undefined when the apply takes their writes. */
  writeBlocker?: ProjectWriteBlocker;

  items: ProjectRoomItem[];

  constructor() {
    this.projectId = 0;
    this.roomKey = '';
    this.kind = 0;
    this.actions = [];
    this.items = [];
  }

  /**
   * Compares the room's own fields and rows; of its part only the scope's completeness and who approves the kickoff
   * documents (the room is replaced on every read).
   */
  isEqual(item: ProjectRoom): boolean {
    if (getRoomPart(item, 'scope')?.documentsComplete !== getRoomPart(this, 'scope')?.documentsComplete) return false;
    if (!isSameDocumentsPart(getRoomPart(item, 'documentsRoom'), getRoomPart(this, 'documentsRoom'))) return false;
    let isEqual: boolean = true;
    if (item.projectId !== this.projectId) isEqual = false;
    if (item.roomKey !== this.roomKey) isEqual = false;
    if (item.deliverableTaskId !== this.deliverableTaskId) isEqual = false;

    if (areDifferent(item.items, this.items, (a: ProjectRoomItem, b: ProjectRoomItem) => a.isEqual(b)) === true) {
      isEqual = false;
    }

    return isEqual;
  }

  getItem(itemId: string): ProjectRoomItem | undefined {
    return this.items.find((item) => item.itemId === itemId);
  }
}

/** The last change of an asset line another person made after the caller read it (PROJECT0131). */
export interface ProjectApplyLineChange {
  /** The line's name now. */
  name: string;

  /** When the line's answer was given (its part's answer.on now). */
  modifiedOn?: Date;

  /** Who changed the line last; undefined when unknown. */
  changedBy?: PersonRef;
}

/**
 * What happened to one route of one item. `ref` is the item id verbatim: every room's items use
 * string ids.
 */
export class ProjectApplyResultItem {
  ref: string;

  /**
   * The projectRoomDecision value this row was dispatched under. Undefined for an unrecognized
   * value; render off outcome/code instead.
   */
  action?: ProjectDecisionWord;

  outcome: ProjectApplyOutcome;

  /** The ControllerException code of a failed row. */
  code?: string;

  /** The set-aside justification, echoed back from the request, when the row was one. */
  reason?: string;

  /** The inquiry task a send created or found. */
  createdTaskId?: number;

  /** Asset answers, add: the item id of a line of the same name in the same container on the list (a hint). */
  sameNameItemId?: string;

  /** Asset answers, adjust and notInUse refused with PROJECT0131: the line as it is now and who changed it last. */
  changed?: ProjectApplyLineChange;

  /** Asset register, refused with PROJECT0049: why; undefined for a value this client cannot name. */
  refusalReason?: ProjectRegisterMergeRefusal;

  /** Documents, approve: what went wrong after the approval, which stands; undefined for a value this client cannot name. */
  warning?: ProjectApplyItemWarning;

  /** Asset inquiry, complete applied: the assessment tasks created for the list's assets (PLAN-075), no starts. */
  createdAssessments?: AssessmentTaskCreated[];

  constructor() {
    this.ref = '';
    this.outcome = 'failed';
  }

  isEqual(item: ProjectApplyResultItem): boolean {
    let isEqual: boolean = true;
    if (item.ref !== this.ref) isEqual = false;
    if (item.action !== this.action) isEqual = false;
    if (item.outcome !== this.outcome) isEqual = false;
    if (item.code !== this.code) isEqual = false;
    if (item.reason !== this.reason) isEqual = false;
    if (item.createdTaskId !== this.createdTaskId) isEqual = false;

    return isEqual;
  }
}

/**
 * The direct result of one POST .../apply call. Nothing is persisted: no id, key, replay flag or
 * version token; it is a transient inline summary. The room's later Done lock comes from
 * `confirmed` (`components/Project/projectProgress.getRoomStatus`), never from this type.
 */
export class ProjectApplyResult {
  appliedCount: number;

  alreadyAppliedCount: number;

  failedCount: number;

  items: ProjectApplyResultItem[];

  /** True exactly when this apply's write left the room's anchor task completed. */
  roomConfirmed: boolean;

  /**
   * Why the room did not confirm, for example `undecided` when items remain without a decision.
   * Undefined when `roomConfirmed` is true, or when the server could not name a reason.
   */
  advanceReason?: string;

  /**
   * The items the apply changed, as the room's read shows them after its writes: a window patches its read with them
   * instead of reading the room again. An item the apply removed is not here (its outcome says so); a room that hands
   * over nothing leaves it empty.
   */
  changedItems: ProjectRoomItem[];

  /**
   * The status of each group of a list the apply touched, by the group's item id (getProjectGroupItemId), as the room's
   * read shows it after its writes: the asset answers hand them over, and a window takes a group's status from here only.
   */
  changedGroups: Map<string, ProjectInquiryGroupStatus>;

  /**
   * When the apply wrote, on the server's clock; undefined for a preview. The inquiry windows order their patches and reads
   * by the write queue and the read count (useProjectInquiryTask), so they do not compare it.
   */
  stamp?: Date;

  constructor() {
    this.appliedCount = 0;
    this.alreadyAppliedCount = 0;
    this.failedCount = 0;
    this.items = [];
    this.roomConfirmed = false;
    this.changedItems = [];
    this.changedGroups = new Map();
  }

  isEqual(item: ProjectApplyResult): boolean {
    let isEqual: boolean = true;
    if (item.appliedCount !== this.appliedCount) isEqual = false;
    if (item.alreadyAppliedCount !== this.alreadyAppliedCount) isEqual = false;
    if (item.failedCount !== this.failedCount) isEqual = false;
    if (item.roomConfirmed !== this.roomConfirmed) isEqual = false;
    if (item.advanceReason !== this.advanceReason) isEqual = false;

    if (
      areDifferent(item.items, this.items, (a: ProjectApplyResultItem, b: ProjectApplyResultItem) => a.isEqual(b)) ===
      true
    ) {
      isEqual = false;
    }

    return isEqual;
  }

  /** The items this apply did not write, as room item ids - the retry scope. */
  getFailedItemIds(): string[] {
    return this.items.filter((item) => item.outcome === 'failed').map((item) => item.ref);
  }
}

/**
 * Whether the risks room's set-aside route can run at all. Checked before a reviewer spends
 * decisions on a batch that would then be refused.
 */
export class ProjectRiskPrecheck {
  /** False means the tenant's risk states cannot express a state change of any kind. */
  riskStatesConfigured: boolean;

  riskStateCount: number;

  /** The state set-aside would move risks into; undefined when this organization has none. */
  archivedRiskStateId?: number;

  /** The one fact the room gates on. False = review freely, but nothing can be set aside. */
  archivedStateAvailable: boolean;

  constructor() {
    this.riskStatesConfigured = false;
    this.riskStateCount = 0;
    this.archivedStateAvailable = false;
  }
}
